import React, { useState, useEffect } from 'react';
import { Card, Button, Badge, Loader } from './shared';
import Tabs from './shared/Tabs';
import { Table, TableHead, TableBody, TableRow, TableCell } from './shared/Table';
import StatCard from './shared/StatCard';

const SecurityMonitor = () => {
  const [summary, setSummary] = useState(null);
  const [firewallBlocks, setFirewallBlocks] = useState([]);
  const [failedLogins, setFailedLogins] = useState([]);
  const [suspiciousProcesses, setSuspiciousProcesses] = useState([]);
  const [networkConnections, setNetworkConnections] = useState([]);
  const [malwareIndicators, setMalwareIndicators] = useState([]);
  const [systemHealth, setSystemHealth] = useState(null);
  const [firewallStatus, setFirewallStatus] = useState(null);
  const [fail2banStatus, setFail2banStatus] = useState(null);
  const [antivirusStatus, setAntivirusStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  const API_BASE = '/api/security';

  useEffect(() => {
    fetchAllData();
    const interval = setInterval(fetchAllData, 10000); // Refresh every 10 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchAllData = async () => {
    try {
      const [summaryRes, blocksRes, loginsRes, processesRes, connectionsRes, malwareRes, healthRes, firewallRes, fail2banRes, antivirusRes] = await Promise.all([
        fetch(`${API_BASE}/summary`),
        fetch(`${API_BASE}/firewall-blocks`),
        fetch(`${API_BASE}/failed-logins`),
        fetch(`${API_BASE}/suspicious-processes`),
        fetch(`${API_BASE}/network-connections`),
        fetch(`${API_BASE}/malware-indicators`),
        fetch(`${API_BASE}/system-health`),
        fetch(`${API_BASE}/firewall-status`),
        fetch(`${API_BASE}/fail2ban-status`),
        fetch(`${API_BASE}/antivirus-status`)
      ]);

      setSummary(await summaryRes.json());
      setFirewallBlocks((await blocksRes.json()).blocks || []);
      setFailedLogins((await loginsRes.json()).attempts || []);
      setSuspiciousProcesses((await processesRes.json()).processes || []);
      setNetworkConnections((await connectionsRes.json()).connections || []);
      setMalwareIndicators((await malwareRes.json()).indicators || []);
      setSystemHealth(await healthRes.json());
      setFirewallStatus(await firewallRes.json());
      setFail2banStatus(await fail2banRes.json());
      setAntivirusStatus(await antivirusRes.json());
      setLoading(false);
    } catch (error) {
      console.error('Error fetching security data:', error);
      setLoading(false);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return '#e74c3c';
      case 'high': return '#e67e22';
      case 'medium': return '#f39c12';
      case 'warning': return '#f39c12';
      case 'low': return '#95a5a6';
      case 'ok': return '#27ae60';
      default: return '#95a5a6';
    }
  };

  const getSeverityBadgeVariant = (severity) => {
    switch (severity) {
      case 'critical': return 'danger';
      case 'high': return 'danger';
      case 'medium': return 'warning';
      case 'warning': return 'warning';
      case 'low': return 'info';
      case 'ok': return 'success';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Loader size="xl" />
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      {/* Header with Status */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h1 style={{ fontSize: '24px', fontWeight: 600, color: '#000000', margin: 0 }}>
            <i className="fas fa-shield-alt" style={{ marginRight: '12px', color: '#FFC65D' }}></i>
            Security Monitor
          </h1>
          <Badge variant={getSeverityBadgeVariant(summary?.overall_status)} size="lg">
            {summary?.overall_status?.toUpperCase() || 'UNKNOWN'}
          </Badge>
        </div>

        {/* Quick Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px' }}>
          <StatCard
            label="Firewall"
            value={summary?.firewall_active ? 'Active' : 'Inactive'}
            icon="fas fa-fire"
            status={summary?.firewall_active ? 'success' : 'error'}
          />

          <StatCard
            label="Fail2Ban"
            value={summary?.fail2ban_active ? 'Active' : 'Inactive'}
            icon="fas fa-shield-alt"
            status={summary?.fail2ban_active ? 'success' : 'error'}
          />

          <StatCard
            label="Antivirus"
            value={summary?.antivirus_installed ? 'Installed' : 'Not Installed'}
            icon="fas fa-virus-slash"
            status={summary?.antivirus_installed ? 'success' : 'warning'}
          />

          <StatCard
            label="Total Threats"
            value={summary?.total_threats || 0}
            icon="fas fa-exclamation-triangle"
            status="warning"
          />

          <StatCard
            label="Blocked Attacks"
            value={summary?.recent_blocks || 0}
            icon="fas fa-ban"
            status="info"
          />

          <StatCard
            label="Malware"
            value={summary?.malware_detected ? 'Detected' : 'Clean'}
            icon="fas fa-bug"
            status={summary?.malware_detected ? 'error' : 'success'}
          />
        </div>
      </div>

      {/* System Health */}
      {systemHealth && (
        <Card title="System Health" style={{ marginBottom: '24px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '13px', color: '#000000' }}>CPU Usage</span>
                <span style={{ fontSize: '13px', fontWeight: 600, color: getSeverityColor(systemHealth.cpu_usage > 80 ? 'critical' : systemHealth.cpu_usage > 60 ? 'warning' : 'ok') }}>
                  {systemHealth.cpu_usage}%
                </span>
              </div>
              <div style={{ width: '100%', height: '8px', backgroundColor: '#e0e0e0', borderRadius: '4px', overflow: 'hidden' }}>
                <div style={{ width: `${systemHealth.cpu_usage}%`, height: '100%', backgroundColor: getSeverityColor(systemHealth.cpu_usage > 80 ? 'critical' : systemHealth.cpu_usage > 60 ? 'warning' : 'ok') }}></div>
              </div>
            </div>

            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '13px', color: '#000000' }}>Memory Usage</span>
                <span style={{ fontSize: '13px', fontWeight: 600, color: getSeverityColor(systemHealth.mem_usage > 80 ? 'critical' : systemHealth.mem_usage > 60 ? 'warning' : 'ok') }}>
                  {systemHealth.mem_usage}%
                </span>
              </div>
              <div style={{ width: '100%', height: '8px', backgroundColor: '#e0e0e0', borderRadius: '4px', overflow: 'hidden' }}>
                <div style={{ width: `${systemHealth.mem_usage}%`, height: '100%', backgroundColor: getSeverityColor(systemHealth.mem_usage > 80 ? 'critical' : systemHealth.mem_usage > 60 ? 'warning' : 'ok') }}></div>
              </div>
            </div>

            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '13px', color: '#000000' }}>Disk Usage</span>
                <span style={{ fontSize: '13px', fontWeight: 600, color: getSeverityColor(systemHealth.disk_usage > 80 ? 'critical' : systemHealth.disk_usage > 60 ? 'warning' : 'ok') }}>
                  {systemHealth.disk_usage}%
                </span>
              </div>
              <div style={{ width: '100%', height: '8px', backgroundColor: '#e0e0e0', borderRadius: '4px', overflow: 'hidden' }}>
                <div style={{ width: `${systemHealth.disk_usage}%`, height: '100%', backgroundColor: getSeverityColor(systemHealth.disk_usage > 80 ? 'critical' : systemHealth.disk_usage > 60 ? 'warning' : 'ok') }}></div>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Tabs */}
      <Tabs
        tabs={[
          { id: 'overview', label: 'Overview' },
          { id: 'firewall', label: 'Firewall' },
          { id: 'protection', label: 'Protection' },
          { id: 'intrusions', label: 'Intrusions' },
          { id: 'malware', label: 'Malware' },
          { id: 'network', label: 'Network' }
        ]}
        activeTab={activeTab}
        onChange={setActiveTab}
        variant="default"
      />

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          {/* Recent Firewall Blocks */}
          <Card title={`Recent Firewall Blocks (${firewallBlocks.length})`}>
            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {firewallBlocks.slice(0, 10).map((block, idx) => (
                <div key={idx} style={{ padding: '8px', borderBottom: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 500, color: '#000000' }}>{block.source_ip}</div>
                    <div style={{ fontSize: '12px', color: '#666666' }}>Port {block.destination_port} • {block.timestamp}</div>
                  </div>
                  <Badge variant={getSeverityBadgeVariant(block.severity)} size="sm">{block.severity}</Badge>
                </div>
              ))}
            </div>
          </Card>

          {/* Failed Login Attempts */}
          <Card title={`Failed Login Attempts (${failedLogins.length})`}>
            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {failedLogins.slice(0, 10).map((login, idx) => (
                <div key={idx} style={{ padding: '8px', borderBottom: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 500, color: '#000000' }}>{login.username}@{login.source_ip}</div>
                    <div style={{ fontSize: '12px', color: '#666666' }}>{login.timestamp}</div>
                  </div>
                  <Badge variant={getSeverityBadgeVariant(login.severity)} size="sm">{login.severity}</Badge>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {activeTab === 'firewall' && (
        <Card title="Firewall Blocks">
          <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '6px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '13px', color: '#666666' }}>Status:</span>
              <span style={{ fontSize: '13px', fontWeight: 600, color: firewallStatus?.active ? '#27ae60' : '#e74c3c' }}>
                {firewallStatus?.status?.toUpperCase()}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px' }}>
              <span style={{ fontSize: '13px', color: '#666666' }}>Active Rules:</span>
              <span style={{ fontSize: '13px', fontWeight: 600, color: '#000000' }}>{firewallStatus?.rules_count || 0}</span>
            </div>
          </div>

          <Table bordered hover>
            <TableHead>
              <TableRow>
                <TableCell header>Source IP</TableCell>
                <TableCell header>Port</TableCell>
                <TableCell header>Time</TableCell>
                <TableCell header>Severity</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {firewallBlocks.map((block, idx) => (
                <TableRow key={idx}>
                  <TableCell>{block.source_ip}</TableCell>
                  <TableCell>{block.destination_port}</TableCell>
                  <TableCell>{block.timestamp}</TableCell>
                  <TableCell>
                    <Badge variant={getSeverityBadgeVariant(block.severity)} size="sm">{block.severity}</Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {activeTab === 'protection' && (
        <div style={{ display: 'grid', gap: '16px' }}>
          {/* Fail2Ban Status */}
          <Card title="Fail2Ban - Intrusion Prevention">
            <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '6px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '13px', color: '#666666' }}>Status:</span>
                <span style={{ fontSize: '13px', fontWeight: 600, color: fail2banStatus?.active ? '#27ae60' : '#e74c3c' }}>
                  {fail2banStatus?.status?.toUpperCase()}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '13px', color: '#666666' }}>Banned IPs:</span>
                <span style={{ fontSize: '13px', fontWeight: 600, color: '#000000' }}>{fail2banStatus?.total_banned || 0}</span>
              </div>
            </div>
            {fail2banStatus?.banned_ips && fail2banStatus.banned_ips.length > 0 && (
              <div>
                <p style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>Currently Banned IPs:</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {fail2banStatus.banned_ips.map((ip, idx) => (
                    <Badge key={idx} variant="danger" size="sm">{ip}</Badge>
                  ))}
                </div>
              </div>
            )}
          </Card>

          {/* Antivirus Status */}
          <Card title="ClamAV - Antivirus Protection">
            <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '6px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '13px', color: '#666666' }}>Status:</span>
                <span style={{ fontSize: '13px', fontWeight: 600, color: antivirusStatus?.installed ? '#27ae60' : '#e74c3c' }}>
                  {antivirusStatus?.status?.toUpperCase()}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '13px', color: '#666666' }}>Version:</span>
                <span style={{ fontSize: '13px', fontWeight: 600, color: '#000000' }}>{antivirusStatus?.version || 'Unknown'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '13px', color: '#666666' }}>Last Update:</span>
                <span style={{ fontSize: '13px', fontWeight: 600, color: '#000000' }}>{antivirusStatus?.last_update || 'Unknown'}</span>
              </div>
            </div>
          </Card>

          {/* Firewall Summary */}
          <Card title="UFW Firewall">
            <div style={{ padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '6px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '13px', color: '#666666' }}>Status:</span>
                <span style={{ fontSize: '13px', fontWeight: 600, color: firewallStatus?.active ? '#27ae60' : '#e74c3c' }}>
                  {firewallStatus?.status?.toUpperCase()}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '13px', color: '#666666' }}>Active Rules:</span>
                <span style={{ fontSize: '13px', fontWeight: 600, color: '#000000' }}>{firewallStatus?.rules_count || 0}</span>
              </div>
            </div>
          </Card>
        </div>
      )}

      {activeTab === 'intrusions' && (
        <Card title="Intrusion Detection">
          <Table bordered hover>
            <TableHead>
              <TableRow>
                <TableCell header>User</TableCell>
                <TableCell header>Source IP</TableCell>
                <TableCell header>Time</TableCell>
                <TableCell header>Severity</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {failedLogins.map((login, idx) => (
                <TableRow key={idx}>
                  <TableCell>{login.username}</TableCell>
                  <TableCell>{login.source_ip}</TableCell>
                  <TableCell>{login.timestamp}</TableCell>
                  <TableCell>
                    <Badge variant={getSeverityBadgeVariant(login.severity)} size="sm">{login.severity}</Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {activeTab === 'malware' && (
        <div style={{ display: 'grid', gap: '16px' }}>
          {malwareIndicators.length > 0 ? (
            malwareIndicators.map((indicator, idx) => (
              <Card key={idx} style={{ backgroundColor: '#FFFFFF', border: '2px solid #e74c3c' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <i className="fas fa-exclamation-circle" style={{ fontSize: '24px', color: '#e74c3c' }}></i>
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: 600, color: '#000000' }}>{indicator.description}</div>
                      <div style={{ fontSize: '12px', color: '#666666', marginTop: '4px' }}>Type: {indicator.type}</div>
                    </div>
                  </div>
                  <Badge variant="danger" size="lg">{indicator.severity?.toUpperCase()}</Badge>
                </div>
              </Card>
            ))
          ) : (
            <Card>
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <i className="fas fa-check-circle" style={{ fontSize: '48px', color: '#27ae60', marginBottom: '16px' }}></i>
                <p style={{ fontSize: '14px', color: '#000000', margin: 0 }}>No malware detected</p>
              </div>
            </Card>
          )}

          {suspiciousProcesses.length > 0 && (
            <Card title="Suspicious Processes">
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #e0e0e0' }}>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#666666' }}>PID</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#666666' }}>User</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#666666' }}>CPU%</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#666666' }}>MEM%</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#666666' }}>Command</th>
                  </tr>
                </thead>
                <tbody>
                  {suspiciousProcesses.map((proc, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid #e0e0e0' }}>
                      <td style={{ padding: '12px', fontSize: '13px', color: '#000000' }}>{proc.pid}</td>
                      <td style={{ padding: '12px', fontSize: '13px', color: '#000000' }}>{proc.user}</td>
                      <td style={{ padding: '12px', fontSize: '13px', color: '#000000' }}>{proc.cpu}%</td>
                      <td style={{ padding: '12px', fontSize: '13px', color: '#000000' }}>{proc.mem}%</td>
                      <td style={{ padding: '12px', fontSize: '13px', color: '#000000', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{proc.command}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
        </div>
      )}

      {activeTab === 'network' && (
        <Card title="Network Connections">
          <Table bordered hover>
            <TableHead>
              <TableRow>
                <TableCell header>Remote IP</TableCell>
                <TableCell header>Remote Port</TableCell>
                <TableCell header>Local Port</TableCell>
                <TableCell header>State</TableCell>
                <TableCell header>Severity</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {networkConnections.map((conn, idx) => (
                <TableRow key={idx}>
                  <TableCell>{conn.remote_ip}</TableCell>
                  <TableCell>{conn.remote_port}</TableCell>
                  <TableCell>{conn.local_port}</TableCell>
                  <TableCell>{conn.state}</TableCell>
                  <TableCell>
                    <Badge variant={getSeverityBadgeVariant(conn.severity)} size="sm">{conn.severity}</Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Refresh Button */}
      <div style={{ marginTop: '24px', textAlign: 'center' }}>
        <Button onClick={fetchAllData} variant="primary">
          <i className="fas fa-sync-alt" style={{ marginRight: '8px' }}></i>
          Refresh Now
        </Button>
      </div>
    </div>
  );
};

export default SecurityMonitor;
